import each from 'lodash/each';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import size from 'lodash/size';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import xor from 'lodash/xor';
import currencyFormatter from 'currency-formatter';
import User from 'yoda-site-components/lib/helpers/User/User';
import { toCapitalize } from 'yoda-site-components/lib/helpers/Utils/utils';
import {
    CHARGE_TYPE_SHIPTOSTORE,
    CHARGE_TYPE_SHIPPING_AND_HANDLING,
    SHIP_TO_STORE_LABELS_STANDARD_DELIVERY_MESSAGE_UPDATED,
    CURRENCY_US,
} from 'yoda-checkout-components/lib/common/Constants';
import { formatPhoneNumber } from 'yoda-account-components/lib/helpers/PhoneNumberFormatter/PhoneNumberFormatter';
import { isAgentLogged } from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    getAfStates,
    getUsStates,
    getMilitaryAddressType,
    getCanadaStates,
} from '../utils/AddressDropDowns';
import { curbSideConstants } from '../helpers/LabelConstantsCheckout';
import {
    validateRequired,
    validateZip,
    validatePhoneNumber,
    validateName,
} from '../helpers/Utils/Utils';

/**
 * Generic service error render component name
 */
const errorHandler = {
    context: 'ADDRESS',
    errorComponent: 'SHIPPING_SECTION',
};

/**
 * Shipping address form fields
 */
const errorFieldMap = {
    firstName: {
        fieldName: 'firstName',
        required: true,
    },
    lastName: {
        fieldName: 'lastName',
        required: true,
    },
    lineOne: {
        fieldName: 'lineOne',
        required: true,
    },
    lineTwo: {
        fieldName: 'lineTwo',
        required: false,
    },
    lineThree: {
        fieldName: 'lineThree',
        required: false,
    },
    zip: {
        fieldName: 'zip',
        required: true,
    },
    city: {
        fieldName: 'city',
        required: true,
    },
    state: {
        fieldName: 'state',
        required: true,
    },
    phone: {
        fieldName: 'phone',
        required: true,
    },
    country: {
        fieldName: 'country',
        required: true,
    },
    company: {
        fieldName: 'company',
        required: false,
    },
};

const pickUpPersonErrorFieldMap = {
    firstName: {
        fieldName: 'firstName',
        required: true,
    },
    lastName: {
        fieldName: 'lastName',
        required: true,
    },
    phone: {
        fieldName: 'phone',
        required: true,
    },
};

/**
 * Return formatted phone number
 * @param value
 * @return {string}
 */
function getFormattedPhoneNumber(value) {
    let phoneNumber = '';
    /* istanbul ignore else */
    if (value) {
        phoneNumber = value.replace(/\D+/g, '') && formatPhoneNumber(value);
    }
    return phoneNumber;
}

/**
 * Override list object based on new component
 * @param list
 * @return {*}
 */
const getFormattedList = (list) => {
    const formattedList = list.map((item) => ({
        key: item.code,
        value: item.name,
    }));
    return formattedList;
};

/**
 * Return Armed Forces states
 * @return {*}
 */
const getArmedForcesStateList = () => {
    const oldFormatAfStates = getAfStates();
    return getFormattedList(oldFormatAfStates);
};

/**
 * Return Canada states
 * @return {*}
 */
const getCanadaStateList = () => {
    const oldFormatStates = getCanadaStates();
    return getFormattedList(oldFormatStates);
};

/**
 * Return US states
 * @return {*}
 */
const getUSStateList = () => {
    const oldFormatStates = getUsStates();
    return getFormattedList(oldFormatStates);
};

/**
 * Return is international address or not based on country value
 * @param country
 * @return {boolean}
 */
const isInternationalAddress = (country) => {
    const USCountries = ['US', 'APO', 'FPO', 'DPO'];
    return !USCountries.includes(country);
};
/**
 * Return empty ship to home form infos
 * @return {}
 */
const getEmptySTHFormInfo = (
    isBillingAddressForm = false,
    internationalCountries,
    truckableItem,
    isUserLoggedInSSR,
    currentAddress = {}
) => {
    const oldFormatMilitaryAddressType = getMilitaryAddressType();
    const country = get(currentAddress, 'country');
    const USCountries = ['APO', 'FPO', 'DPO', 'US'];
    const isInternational = USCountries.indexOf(country) < 0; // If International then US will be default dropdown
    const usStateList =
        !country || /us/gi.test(country) || isInternational
            ? getUSStateList()
            : getArmedForcesStateList();
    let militaryAddressTypeList = [];
    const isExistingAPAddress = get(currentAddress, 'country') === 'AP';
    /* istanbul ignore else */
    if (!truckableItem || isExistingAPAddress) {
        militaryAddressTypeList = getFormattedList(oldFormatMilitaryAddressType);
    }
    militaryAddressTypeList.unshift({
        key: 'US',
        value: 'United States',
    });
    let isUserLoggedIn = User.isUserLoggedIn();
    if (__SERVER__) {
        isUserLoggedIn = isUserLoggedInSSR;
    }
    const emptySTHFormInfo = {
        address: {
            country: 'US',
        },
        addressTouchedFields: {},
        addressFormErrors: {},
        usStateList,
        militaryAddressTypeList,
        showCompanyName: false,
        showMilitaryAddressFields: false,
        showExpressCheckoutCheckBox: isUserLoggedIn,
        isBillingAddressForm,
    };
    if (internationalCountries) {
        /**
         * Show international country list in billing address
         * @type {*}
         */
        let internationalCountryList = getFormattedList(internationalCountries.slice(1));
        internationalCountryList = militaryAddressTypeList.concat(internationalCountryList);
        emptySTHFormInfo.militaryAddressTypeList = internationalCountryList;
    }
    return emptySTHFormInfo;
};

/**
 * Clear address filed if country changed from US to AP
 * @param address
 * @return {*}
 */
const clearAddressField = (address) => {
    const modifyAddress = address;
    modifyAddress.lineOne = '';
    modifyAddress.lineTwo = '';
    modifyAddress.zip = '';
    modifyAddress.state = '';
    modifyAddress.city = '';
    return modifyAddress;
};

/**
 * Get Order shipping address
 * @param orderDetails
 * @return {*}
 */
const getOrderShippingAddress = (orderDetails) => {
    const deliveryGroups = get(orderDetails, 'data.delivery.groups', []);
    return find(deliveryGroups, { type: 'HOME' }) || find(deliveryGroups, { type: 'STORE' }) || {};
};

/**
 * Get Order ship to home address
 * @param orderDetails
 * @return {*}
 */
const getOrderShipToHomeAddress = (orderDetails) => {
    const deliveryGroups = get(orderDetails, 'data.delivery.groups', []);
    return find(deliveryGroups, { type: 'HOME' }) || {};
};

/**
 * Get Order ship to store address
 * @param orderDetails
 * @return {*}
 */
const getOrderShipToStoreAddress = (orderDetails) => {
    const deliveryGroups = get(orderDetails, 'data.delivery.groups', []);
    return find(deliveryGroups, { type: 'STORE' }) || {};
};

/**
 * Get BOPIS delivery address
 * @param orderDetails
 * @return {{}}
 */
const getOrderBOPISInfo = (orderDetails) => {
    const deliveryGroups = get(orderDetails, 'data.delivery.groups', []);
    return find(deliveryGroups, { type: 'BOPIS' }) || {};
};

/**
 * Check order is eligible for ship to store or not
 * @return {boolean}
 */
const checkShipToHomeOnly = (items) => {
    let isShipToHomeOnly = true;
    forEach(items, (item) => {
        const storeOption = find(item.deliveryOptions, { code: 'STORE', status: 'AVAILABLE' });
        // for b2b agent show only ship to home
        if (!isEmpty(storeOption) && !isAgentLogged()) {
            isShipToHomeOnly = false;
        }
    });
    return isShipToHomeOnly;
};

/**
 * Check truck item exist in order or not
 * @param items
 * @return {boolean}
 */
const checkTruckableItemInBag = (items) => !!find(items, { product: { truckable: true } });

/**
 * Return order shipping address as per UI
 * @param address
 * @return {}
 */
const getOrderShippingAddressUI = (address) => {
    let addressLineOne = toCapitalize(address.lineOne);
    const city = address.city ? toCapitalize(address.city) : address.militaryAddressType;
    const addressLineTwo = `${city}, ${address.state} ${address.zip}`;
    if (address.lineTwo) {
        addressLineOne = `${addressLineOne}, ${address.lineTwo}`;
    }
    const orderAddress = {
        userName: `${toCapitalize(address.firstName)} ${toCapitalize(address.lastName)}`,
        company: `${toCapitalize(address.company)}`,
        addressLineOne,
        addressLineTwo,
        phone: getFormattedPhoneNumber(address.phone),
        source: address,
    };
    return orderAddress;
};

/**
 * Return order shipping method as per UI
 * @param serviceLevels
 * @return {}
 */
const getOrderShippingMethodUI = (serviceLevels) => {
    const serviceLevelList = serviceLevels.data;
    const shippingMethodList = [];
    each(serviceLevelList, (serviceLevel) => {
        const shippingMethod = {
            label: serviceLevel.duration,
            charge: currencyFormatter.format(serviceLevel.charge, { code: CURRENCY_US }),
            message: toCapitalize(serviceLevel.message),
            selected: serviceLevel.selected,
            code: serviceLevel.code,
            isFastestDelivery: false,
        };
        shippingMethodList.push(shippingMethod);
    });
    const lastIndex = size(shippingMethodList) - 1;
    if (lastIndex > 0 && shippingMethodList[lastIndex].selected) {
        shippingMethodList[lastIndex].isFastestDelivery = true;
    }
    return shippingMethodList;
};

/**
 * Check truck shipping method present or not
 * @param serviceLevels
 * @return {boolean}
 */
const isTruckShippingMethod = (serviceLevels) => {
    const shippingMethodsList = serviceLevels.data;
    const truckShippingMethod = find(shippingMethodsList, { code: 'TRUCK' });
    return !!truckShippingMethod;
};

/**
 * Check holiday shipping true or false
 * @param serviceLevels
 * @return {boolean}
 */
const isHolidayShippingEligible = (serviceLevels) => {
    const shippingMethodsList = serviceLevels.data;
    const holidayShipping = find(shippingMethodsList, { holidayShipping: true });
    return !!holidayShipping;
};

/**
 * Update user selected shipping method
 * @param shippingMethodList
 * @param selectedCode
 */
const updateSelectedShippingMethod = (shippingMethodList, selectedCode) => {
    const updatedShippingMethodList = [];
    each(shippingMethodList, (shippingMethod) => {
        const updatedShippingMethod = shippingMethod;
        updatedShippingMethod.selected = updatedShippingMethod.code === selectedCode;
        updatedShippingMethod.isFastestDelivery = false;
        updatedShippingMethodList.push(updatedShippingMethod);
    });
    const lastIndex = size(updatedShippingMethodList) - 1;
    if (lastIndex > 0 && updatedShippingMethodList[lastIndex].selected) {
        updatedShippingMethodList[lastIndex].isFastestDelivery = true;
    }
    return updatedShippingMethodList;
};

/**
 * Update user selected shipping address
 * @param shippingAddressList
 * @param selectedAddress
 */
const updateSelectedShippingAddressUI = (shippingAddressList, selectedAddress) => {
    const updatedShippingAddressList = [];
    each(shippingAddressList, (shippingAddress) => {
        const updatedShippingAddress = shippingAddress;
        updatedShippingAddress.selected =
            updatedShippingAddress.source.id === selectedAddress.source.id;
        updatedShippingAddressList.push(updatedShippingAddress);
    });
    return updatedShippingAddressList;
};

/**
 * Return register user shipping address list as per UI
 * @param addressList
 * @param orderAddress
 * @return {Array}
 */
const getRegisterUserShippingAddressUI = (addressList, orderAddress) => {
    const updatedAddressList = [];
    const orderRelationshipId = get(orderAddress, 'relationships.accountAddress.id');
    let isTransitAddressExist = true;
    const isExpressAddressExist = !!find(addressList, { expressCheckout: true });
    let defaultAddress = null;
    let expressAddress = null;
    each(addressList, (address) => {
        // eslint-disable-next-line no-param-reassign
        address.lineOne = address.addressLineOne;
        // eslint-disable-next-line no-param-reassign
        address.lineTwo = address.addressLineTwo;
        const formattedAddress = getOrderShippingAddressUI(address);
        formattedAddress.selected = false;
        const isExpressAddress = isExpressAddressExist
            ? address.expressCheckout
            : address.isDefault;
        if (isEmpty(orderAddress)) {
            formattedAddress.selected = isExpressAddress;
        }
        formattedAddress.showDefaultLabel = isExpressAddress;
        formattedAddress.showDeleteOption = !address.isDefault;
        if (address.id === orderRelationshipId) {
            formattedAddress.selected = true;
            isTransitAddressExist = false;
        }
        if (address.isDefault && !address.expressCheckout) {
            defaultAddress = formattedAddress;
        } else if (address.expressCheckout) {
            expressAddress = formattedAddress;
        } else {
            updatedAddressList.push(formattedAddress);
        }
    });
    defaultAddress && updatedAddressList.unshift(defaultAddress);
    expressAddress && updatedAddressList.unshift(expressAddress);
    /**
     * Add transient address into list
     */
    if (!isEmpty(orderAddress) && isTransitAddressExist) {
        const orderAddressUI = getOrderShippingAddressUI(orderAddress);
        orderAddressUI.selected = true;
        orderAddressUI.showDeleteOption = false;
        updatedAddressList.unshift(orderAddressUI);
    }
    return updatedAddressList;
};

/**
 * Check show more address link need to show or not
 * @return {boolean}
 */
const checkShowMoreAddressLink = (userAddressList) => {
    let showMore = false;
    if (userAddressList.length > 3) {
        const index = findIndex(userAddressList, (address) => address.selected);
        showMore = index < 3;
    }
    return showMore;
};

/**
 * Check if pickup information value is got updated or not
 * @param pickupPersonInfo
 * @param orderPickupPersonInfo
 * @return {boolean}
 */
const isPickupPersonInfoChanged = (pickupPersonInfo, orderPickupPersonInfo) => {
    let isPickUpPersonInfoValueChanged = false;
    each(Object.keys(pickUpPersonErrorFieldMap), (fieldName) => {
        let newValue = pickupPersonInfo[fieldName];
        if (fieldName === 'phone') {
            newValue = newValue.replace(/\D+/g, '');
        }
        if (newValue !== orderPickupPersonInfo[fieldName]) {
            isPickUpPersonInfoValueChanged = true;
        }
    });
    return isPickUpPersonInfoValueChanged;
};

/**
 * Return error message based on shipping address form input field
 * @param fieldName, value
 * @return string
 */
const validateShippingFormField = (
    fieldName,
    value,
    isZipInvalidFromAPI = false,
    validateLineTwo
) => {
    const fieldValue = value && value.trim();
    const errorField = errorFieldMap[fieldName];
    if (fieldName === 'lineTwo') {
        errorField.required = !!validateLineTwo;
    }
    if (!errorField.required) {
        return '';
    }
    let error = validateRequired(fieldValue, errorField.fieldName);
    if (!error) {
        if (fieldName === 'zip') {
            error = validateZip(fieldValue, isZipInvalidFromAPI);
        } else if (fieldName === 'phone') {
            error = validatePhoneNumber(fieldValue);
        } else if (fieldName === 'firstName' || fieldName === 'lastName') {
            error = validateName(fieldValue, fieldName);
        }
    }
    return error || '';
};

/**
 * Return street address error message
 */
const isValidStreetAddress = (lineOne, country) => {
    const streetRegex = /^(\s*)?[a-z]*\d+[a-z]*/gi;
    const poBoxRegex = /^(\s*)?P(\.|\s)*O(\.|\s)*BOX/gi;
    const rrRegex = /^(\s*)?(r\s*r|route|rt)/gi;
    const highwayRegex = /^(\s*)?(hc|hcr)/gi;
    return (
        poBoxRegex.test(lineOne) ||
        rrRegex.test(lineOne) ||
        highwayRegex.test(lineOne) ||
        country !== 'US' ||
        streetRegex.test(lineOne)
    );
};

/**
 * Detect PO Box present in form fields
 */
const isPatternPresent = (value = '', patterns = []) => {
    const normalized = value
        .toLowerCase()
        .replace(/[.\-_,]/g, '') // remove punctuation
        .replace(/\s+/g, ' '); // normalize spaces

    return patterns.some((pattern) => normalized.includes(pattern));
};

/**
 * Validate shipping address form input value and return addressFormErrors
 * @param address
 * @return addressFormErrors
 */
const validateShippingAddressForm = (
    address,
    skipFields = [],
    isZipInvalidFromAPI,
    subPremisesInvalid
) => {
    const addressFormErrors = {};
    let errorFieldMapKey = Object.keys(errorFieldMap);
    /**
     * Skip city check for APO/FPO/DPO address
     */
    if (address.country !== 'US' && !isInternationalAddress(address.country)) {
        skipFields.push('city');
    }
    if (!isEmpty(skipFields)) {
        errorFieldMapKey = xor(errorFieldMapKey, skipFields);
    }
    each(errorFieldMapKey, (fieldName) => {
        if (errorFieldMap[fieldName].required || (subPremisesInvalid && fieldName === 'lineTwo')) {
            const error = validateShippingFormField(
                fieldName,
                address[fieldName],
                isZipInvalidFromAPI,
                subPremisesInvalid && fieldName === 'lineTwo'
            );
            if (error) {
                addressFormErrors[fieldName] = error;
            }
        }
    });
    return addressFormErrors;
};

/**
 * Validate ship to store form input value and return addressFormErrors
 * @param pickupPersonInfo
 * @return pickupPersonFormErrors
 */
const validateShipToStoreForm = (pickupPersonInfo) => {
    const pickupPersonFormErrors = {};
    each(Object.keys(pickUpPersonErrorFieldMap), (fieldName) => {
        const error = validateShippingFormField(fieldName, pickupPersonInfo[fieldName]);
        if (error) {
            pickupPersonFormErrors[fieldName] = error;
        }
    });
    return pickupPersonFormErrors;
};

/**
 * Return order store UI map
 * @param orderStoreInfo
 * @return {{addressLineTwo: string, phone: string, name: string, addressLineOne: string,
 * timings: array, storeId: string}}
 */
const getStoreInfoUI = (orderStoreInfo, fromProfile) => {
    const pickUpStoreInfo = {
        storeId: orderStoreInfo.id,
        name: orderStoreInfo.name,
        addressLineOne: orderStoreInfo.street,
        addressLineTwo: `${orderStoreInfo.city}, ${orderStoreInfo.state} ${orderStoreInfo.zip}`,
        phone: orderStoreInfo.phone,
        timings: orderStoreInfo.timings,
        selected: true,
    };
    pickUpStoreInfo.phone = getFormattedPhoneNumber(pickUpStoreInfo.phone);
    pickUpStoreInfo.name = `${orderStoreInfo.city}, ${orderStoreInfo.state} – ${orderStoreInfo.name}`;
    pickUpStoreInfo.isCurbSideEligible = (orderStoreInfo.services || []).includes(
        curbSideConstants.CURBSIDE_PICKUP
    );
    if (fromProfile) {
        const { address } = orderStoreInfo;
        pickUpStoreInfo.addressLineOne = address.lineOne;
        pickUpStoreInfo.addressLineTwo = `${address.city}, ${address.state} ${address.zip}`;
        pickUpStoreInfo.selected = false;
        pickUpStoreInfo.isMyStore = orderStoreInfo.isDefault;
        pickUpStoreInfo.name = `${address.city}, ${address.state} - ${orderStoreInfo.name}`;
    }
    return pickUpStoreInfo;
};

/**
 * Return ship to store delivery charge amount
 * @param orderDetails
 */
function getShipToStoreCharge(orderDetails) {
    const deliveryGroups = get(orderDetails, 'data.delivery.groups', []);
    const isSTSAddedInOrder = find(deliveryGroups, { type: 'STORE' });
    let deliveryChargeItem = find(orderDetails.data.charges, {
        type: CHARGE_TYPE_SHIPPING_AND_HANDLING,
    });
    if (!isSTSAddedInOrder) {
        deliveryChargeItem = find(orderDetails.data.altCharges, { type: CHARGE_TYPE_SHIPTOSTORE });
    }
    return deliveryChargeItem?.amount;
}

/**
 * Return order store UI map list based on user store list and order store
 * @param storeInfo
 * @param userStoreList
 * @return {Array}
 */
function getPickupStoreList(storeInfo, userStoreList, selectedStoreId) {
    const pickupStoreList = [];
    if (storeInfo && storeInfo.name) {
        pickupStoreList.push(storeInfo);
    }
    each(userStoreList, (userStore) => {
        const formattedStoreInfo = getStoreInfoUI(userStore, true);
        if (selectedStoreId) {
            /**
             * If register user add store
             */
            formattedStoreInfo.selected = userStore.id === selectedStoreId;
        } else if (storeInfo.storeId) {
            /**
             * order store present in register user store list
             */
            formattedStoreInfo.selected = userStore.id === storeInfo.storeId;
        } else {
            /**
             * No store in orders
             */
            formattedStoreInfo.selected = userStore.isDefault;
        }
        pickupStoreList.push(formattedStoreInfo);
    });
    const isDefaultStoreSelected = find(pickupStoreList, { selected: true });
    if (!isDefaultStoreSelected && pickupStoreList.length > 0) {
        pickupStoreList[0].selected = true;
    }
    return pickupStoreList;
}

/**
 * Return pickup person information UI map
 * @param pickup
 * @return {{firstName: string, lastName: string, phone: string}}
 */
function getPickupPersonInfo(pickup = {}) {
    const pickupPersonInfo = {
        firstName: pickup.firstName,
        lastName: pickup.lastName,
        phone: '',
    };
    if (pickup.phone) {
        pickupPersonInfo.phone = getFormattedPhoneNumber(pickup.phone);
    }
    return pickupPersonInfo;
}

/**
 * Update user selected store in user profile store list
 * @param selectedStoreId
 * @param userStoreList
 * @return {Array}
 */
function updateSelectedStoreUI(selectedStoreId, userStoreList) {
    const pickupStoreList = [];
    each(userStoreList, (userStore) => {
        // eslint-disable-next-line no-param-reassign
        userStore.selected = userStore.storeId === selectedStoreId;
        pickupStoreList.push(userStore);
    });
    return pickupStoreList;
}

/**
 * Return init ship to store section base UI
 * @param orderDetailsInfo
 * @return {object}
 */
function initShipToStoreSectionUI(orderDetailsInfo, isUserLoggedIn) {
    const shipToStoreInfo = {
        shippingMethod: {
            label: SHIP_TO_STORE_LABELS_STANDARD_DELIVERY_MESSAGE_UPDATED,
            message: getShipToStoreCharge(orderDetailsInfo),
        },
        pickupPersonInfo: {},
        pickupPersonInfoErrors: {},
        pickupPersonInfoTouchedFields: {},
        showAddNewStoreOption: isUserLoggedIn,
    };
    return shipToStoreInfo;
}

/**
 * Check if user updated BOPIS information or not
 * @param userInputBopisInfo
 * @param orderDetailsInfo
 * @return {boolean}
 */
function isBopisValueUpdated(userInputBopisInfo, orderBopisInfo) {
    const updatedBopisInfo = { ...userInputBopisInfo };
    if (updatedBopisInfo.phone) {
        updatedBopisInfo.phone = updatedBopisInfo.phone.replace(/\D+/g, '');
    }
    return !isEqual(orderBopisInfo, updatedBopisInfo);
}

const getPayloadForSkipShippingMethod = (shippingMethodCode) => {
    const payload = {};
    if (shippingMethodCode) {
        payload.serviceLevel = {
            code: shippingMethodCode,
            selected: true,
        };
    }
    return payload;
};

const getUpdateShippingAddressToOrderPayload = (
    id,
    address,
    xStandardizationNotRequired,
    isLoadedFromForm,
    shippingMethodCode = ''
) => {
    const requestPayload = {
        payload: {
            id,
            params: {
                type: 'HOME',
                ...getPayloadForSkipShippingMethod(shippingMethodCode),
            },
            errorHandler,
        },
    };
    requestPayload.payload.params.address = { ...address };
    requestPayload.payload.params.address.phone =
        (address.phone && address.phone.replace(/\D+/g, '')) || '';
    if (address.country !== 'US' && isLoadedFromForm) {
        requestPayload.payload.params.address.city = address.country;
        requestPayload.payload.params.address.militaryAddressType = address.country;
        requestPayload.payload.params.address.country = 'AP';
        !requestPayload.payload.params.address.lineTwo &&
            delete requestPayload.payload.params.address.lineTwo;
        requestPayload.payload.isXStandardizationRequired = 'false';
    }
    if (xStandardizationNotRequired) {
        requestPayload.payload.isXStandardizationRequired = 'false';
    }
    delete requestPayload.payload.params.address.relationships;
    return requestPayload;
};

const getUpdateShipToStoreToOrderPayload = (id, storeInfo, pickupPersonInfo) => {
    const requestPayload = {
        payload: {
            id,
            params: {
                type: 'STORE',
                store: {
                    id: storeInfo.storeId,
                },
                pickup: pickupPersonInfo,
            },
            errorHandler,
        },
    };
    requestPayload.payload.params.pickup.phone = pickupPersonInfo.phone.replace(/\D+/g, '');
    return requestPayload;
};

const getChangeShippingAddressToOrderPayload = (
    orderShippingGroupId,
    relationshipsId,
    shippingMethodCode = ''
) => ({
    payload: {
        params: {
            address: {
                relationships: {
                    accountAddress: {
                        id: relationshipsId,
                    },
                },
            },
            ...getPayloadForSkipShippingMethod(shippingMethodCode),
        },
        id: orderShippingGroupId,
        errorHandler,
    },
});

const getUpdateShippingMethodToOrderPayload = (id, selectedShippingMethod) => ({
    payload: {
        params: {
            selected: true,
        },
        code: selectedShippingMethod.code,
        id,
        errorHandler,
    },
});

const getAddShippingAddressToProfilePayload = (address, xStandardizationNotRequired) => {
    const requestPayload = {
        payload: {
            params: {
                firstName: address.firstName,
                lastName: address.lastName,
                company: address.company,
                addressLineOne: address.lineOne,
                state: address.state,
                city: address.city,
                zip: address.zip,
                country: address.country,
                phone: address.phone.replace(/\D+/g, ''),
                expressCheckout: address.expressCheckout,
            },
            errorHandler,
            isXStandardizationRequired: xStandardizationNotRequired ? 'false' : 'true',
        },
    };
    if (address.id) {
        requestPayload.payload.addressId = address.id;
    }
    if (address.lineTwo) {
        requestPayload.payload.params.addressLineTwo = address.lineTwo;
    }
    /* istanbul ignore else */
    if (address.country !== 'US') {
        requestPayload.payload.params.militaryAddressType = address.country;
        requestPayload.payload.params.country = 'AP';
        delete requestPayload.payload.params.city;
        requestPayload.payload.isXStandardizationRequired = 'false';
    }
    return requestPayload;
};

const getDeleteShippingAddressFromProfilePayload = (addressId) => ({
    payload: {
        addressId,
        errorHandler,
    },
});

const getShippingAddressFromProfilePayload = (headers, notShipToOtherCountry) => ({
    payload: {
        errorHandler,
        notShipToOtherCountry,
    },
    headers,
    apiName: 'GET_CUSTOMER_ADDRESSES_API',
});

const getAddStoreToProfilePayload = (storeId) => ({
    payload: {
        id: storeId,
        errorHandler,
    },
});

const getSubmitBOPISPayload = (pickupPersonInfo, orderBopisInfo) => {
    const requestPayload = {
        payload: {
            errorHandler: {
                context: 'PICKUP',
                errorComponent: 'SAMEDAYPICKUP_SECTION',
            },
            id: orderBopisInfo.id,
            params: pickupPersonInfo,
        },
    };
    /* istanbul ignore else */
    if (!pickupPersonInfo.alternatePerson) {
        requestPayload.payload.params = {
            alternatePerson: false,
        };
    }
    return requestPayload;
};

const isNewRegisterAutoFillEligible = (context, userAddressList) => {
    return !__SERVER__ && User.isUserLoggedIn() && userAddressList.length === 0;
};

/**
 * Return true if address is military address based on city name else return false
 * @param city
 * @return {boolean}
 */
const isMilitaryAddress = (city) => {
    if (!city) {
        return false;
    }
    const militaryAddressList = ['APO', 'FPO', 'DPO'];
    const cityToCheck = city.toUpperCase();
    return militaryAddressList.includes(cityToCheck);
};

/**
 * Return true if ZipCode, State and City field empty else return false
 * @param userInputAddress
 * @return {boolean}
 */
const isZipCodeStateAndCityEmpty = (userInputAddress) => {
    const address = get(userInputAddress, 'address', {});
    const { zip, city, state } = address;
    return !zip && !city && !state;
};

/**
 * Return code for user selected shipping method.
 * @param shipping
 * @return {string}
 */
const getUserSelectedShippingMethod = (shipping) => {
    const shippingMethods = get(shipping, 'shipToHomeInfo.shippingMethods', []);
    const selectedShippingMethod =
        !isEmpty(shippingMethods) && find(shippingMethods, { selected: true });
    return !isEmpty(selectedShippingMethod) && selectedShippingMethod.code;
};

const getPromiseDeliveryDates = (items, isOcpPage) => {
    const deleiveryDates = {
        bopis: {
            items: [],
            storeName: '',
        },
        regular: {},
        truckable: {
            label: 'Est. Delivery',
        },
    };
    forEach(items, (value) => {
        const selectedDelivery = find(value?.deliveryOptions, { selected: true });
        const isTruckable = get(value, 'product.truckable', false);
        if (selectedDelivery.code === 'BOPIS') {
            deleiveryDates.bopis.storeName = selectedDelivery.storeName || '';
            deleiveryDates.bopis.items.push({
                product: value?.product,
                quantity: value?.quantity,
            });
        } else if (
            !isTruckable &&
            !deleiveryDates.regular[selectedDelivery?.delivery?.deliveryDate]
        ) {
            deleiveryDates.regular[selectedDelivery?.delivery?.deliveryDate] = {
                label: selectedDelivery.code === 'STORE' ? 'Estimated Pick Up: ' : '',
                date: selectedDelivery?.delivery?.deliveryDate,
                deliveryPromiseMessage: selectedDelivery?.deliveryPromiseMessage,
                deliveryCode: selectedDelivery.code,
                storeName: selectedDelivery?.storeName || '',
                items: [
                    {
                        product: value?.product,
                        quantity: value?.quantity,
                    },
                ],
            };
        } else if (isTruckable) {
            if (!isOcpPage) {
                deleiveryDates.truckable.deliveryPromiseMessage =
                    selectedDelivery?.deliveryPromiseMessage;
                if (!deleiveryDates.truckable.items) {
                    deleiveryDates.truckable.items = [];
                }
                deleiveryDates.truckable.items.push({
                    product: value?.product,
                    quantity: value?.quantity,
                });
            } else {
                /* istanbul ignore else */
                if (!deleiveryDates.truckable.ocpGroupItems) {
                    deleiveryDates.truckable.ocpGroupItems = {};
                }
                const [, deliveryMsg] = selectedDelivery?.deliveryPromiseMessage.split('</strong>');
                /* istanbul ignore else */
                if (deliveryMsg) {
                    const keyName = deliveryMsg?.split(' ').join('');
                    /* istanbul ignore else */
                    if (!deleiveryDates.truckable.ocpGroupItems[keyName]) {
                        deleiveryDates.truckable.ocpGroupItems[keyName] = {};
                        deleiveryDates.truckable.ocpGroupItems[keyName].label = deliveryMsg;
                        deleiveryDates.truckable.ocpGroupItems[keyName].items = [];
                    }
                    deleiveryDates.truckable.ocpGroupItems[keyName].items.push({
                        product: value?.product,
                        quantity: value?.quantity,
                    });
                }
            }
        } else {
            selectedDelivery.code === 'BOPIS'
                ? deleiveryDates.bopis[selectedDelivery?.delivery?.pickupDate].items.push({
                      product: value?.product,
                      quantity: value?.quantity,
                  })
                : deleiveryDates.regular[selectedDelivery?.delivery?.deliveryDate].items.push({
                      product: value?.product,
                      quantity: value?.quantity,
                  });
        }
    });
    return deleiveryDates;
};

export default {
    getUSStateList,
    getArmedForcesStateList,
    getCanadaStateList,
    isInternationalAddress,
    getEmptySTHFormInfo,
    clearAddressField,
    getOrderShippingAddress,
    getOrderShipToHomeAddress,
    getOrderShipToStoreAddress,
    getOrderBOPISInfo,
    checkShipToHomeOnly,
    getOrderShippingAddressUI,
    getOrderShippingMethodUI,
    isTruckShippingMethod,
    isHolidayShippingEligible,
    getRegisterUserShippingAddressUI,
    updateSelectedShippingMethod,
    isValidStreetAddress,
    isPatternPresent,
    validateShippingFormField,
    validateShippingAddressForm,
    validateShipToStoreForm,
    updateSelectedShippingAddressUI,
    checkShowMoreAddressLink,
    isPickupPersonInfoChanged,
    getStoreInfoUI,
    getPickupStoreList,
    getPickupPersonInfo,
    updateSelectedStoreUI,
    initShipToStoreSectionUI,
    getFormattedPhoneNumber,
    isBopisValueUpdated,
    getUpdateShippingAddressToOrderPayload,
    getUpdateShippingMethodToOrderPayload,
    getChangeShippingAddressToOrderPayload,
    getAddShippingAddressToProfilePayload,
    getDeleteShippingAddressFromProfilePayload,
    getShippingAddressFromProfilePayload,
    getUpdateShipToStoreToOrderPayload,
    getAddStoreToProfilePayload,
    getSubmitBOPISPayload,
    checkTruckableItemInBag,
    isNewRegisterAutoFillEligible,
    isMilitaryAddress,
    getShipToStoreCharge,
    isZipCodeStateAndCityEmpty,
    getUserSelectedShippingMethod,
    getPromiseDeliveryDates,
};
